/**
 * §26.12/§26.12a — declaring media spend on a spend-share deal, and seeing
 * the state of every claim.
 *
 * §6.7c is the whole shape of this screen: the platform verifies nothing an
 * affiliate declares, so `spend.intro` and every state chip below say that
 * plainly rather than implying the figure typed here is what gets paid. Only
 * an operator's decision (`state: APPROVED`, `approved_minor` set) is ever
 * priced, and never above what was declared (0208's own CHECK) — this screen
 * has no path that writes `approved_minor` at all, the same reason
 * `setMyTrafficSources` carries no verification write either.
 */
import { useState } from 'react';

import {
  Callout,
  Chip,
  Empty,
  Field,
  Loading,
  Panel,
  Select,
  TableScroll,
  TextInput,
  explainFailure,
  formatDate,
  kitText,
  useLoad,
} from '@affdice/portal-kit';

import { formatSpendAmount, type AffiliateApi } from './client.ts';
import { t } from './messages.ts';

export function SpendDeclarations({ api }: { api: AffiliateApi }) {
  const [version, setVersion] = useState(0);
  const terms = useLoad(() => api.terms(), []);
  const statements = useLoad(() => api.statements(), []);
  const declarations = useLoad(() => api.spendDeclarations(), [version]);

  const [dealId, setDealId] = useState('');
  const [periodId, setPeriodId] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [evidenceUrl, setEvidenceUrl] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);

  if (terms.state === 'loading' || statements.state === 'loading' || declarations.state === 'loading') {
    return <Loading rows={3} />;
  }
  if (terms.state === 'error') return <Callout tone="error">{explainFailure(terms.error).message}</Callout>;
  if (statements.state === 'error') {
    return <Callout tone="error">{explainFailure(statements.error).message}</Callout>;
  }
  if (declarations.state === 'error') {
    return <Callout tone="error">{explainFailure(declarations.error).message}</Callout>;
  }

  // Only a live SPEND_SHARE deal is worth offering — declaring against any
  // other model has nothing to price (declareSpend refuses it, §26.12).
  const spendDeals = terms.value.items.filter((row) => row.commission_type === 'SPEND_SHARE');
  const periods = statements.value.data;

  const dealOptions = spendDeals.map((d) => ({ value: d.deal_id, label: `${d.brand} — ${d.deal_code}` }));
  const periodOptions = periods.map((p) => ({
    value: p.period_id,
    label: `${formatDate(p.period_from)} – ${formatDate(p.period_to)}`,
  }));
  const chosenDeal = dealOptions.some((o) => o.value === dealId) ? dealId : (dealOptions[0]?.value ?? '');
  const chosenPeriod = periodOptions.some((o) => o.value === periodId) ? periodId : (periodOptions[0]?.value ?? '');

  const declare = () => {
    if (!chosenDeal || !chosenPeriod) return;
    setBusy(true);
    setFailure(null);
    setSaved(null);
    api
      .declareSpend({
        deal_id: chosenDeal,
        period_id: chosenPeriod,
        declared_minor: amount.trim(),
        currency: currency.trim().toUpperCase(),
        ...(evidenceUrl.trim() ? { evidence_url: evidenceUrl.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      })
      .then((r) => {
        setSaved(r.message);
        setAmount('');
        setEvidenceUrl('');
        setNote('');
        setVersion((v) => v + 1);
      })
      .catch((e: unknown) => setFailure(e))
      .finally(() => setBusy(false));
  };

  return (
    <div className="form">
      {failure ? <Callout tone="error">{explainFailure(failure).message}</Callout> : null}
      <Callout fold={false}>{t('spend.intro')}</Callout>

      {spendDeals.length === 0 ? (
        <Empty>{t('spend.noDeals')}</Empty>
      ) : periods.length === 0 ? (
        <Empty>{t('spend.noPeriods')}</Empty>
      ) : (
        <>
          <div className="form-row">
            <Field label={t('spend.deal')}>
              <Select value={chosenDeal} onChange={setDealId} options={dealOptions} />
            </Field>
            <Field label={t('spend.period')}>
              <Select value={chosenPeriod} onChange={setPeriodId} options={periodOptions} />
            </Field>
          </div>
          <div className="form-row">
            <Field label={t('spend.amount')} hint={t('spend.amount.hint')}>
              <TextInput value={amount} onChange={setAmount} placeholder={t('spend.amount.placeholder')} />
            </Field>
            <Field label={t('spend.currency')}>
              <TextInput value={currency} onChange={(v) => setCurrency(v.toUpperCase())} />
            </Field>
          </div>
          <Field label={t('spend.evidenceUrl')} hint={t('spend.evidenceUrl.hint')}>
            <TextInput value={evidenceUrl} onChange={setEvidenceUrl} placeholder="https://…" />
          </Field>
          <Field label={t('spend.note')}>
            <TextInput value={note} onChange={setNote} />
          </Field>
          <div className="form-actions">
            <button
              type="button"
              className="primary"
              disabled={busy || !amount.trim() || !currency.trim()}
              onClick={declare}
            >
              {busy ? kitText('action.saving') : t('spend.declare')}
            </button>
          </div>
          {saved ? <Callout fold={false}>{saved}</Callout> : null}
        </>
      )}

      <Panel title={t('spend.history')}>
        {declarations.value.items.length === 0 ? (
          <Empty>{t('spend.empty')}</Empty>
        ) : (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <th>{t('spend.col.deal')}</th>
                  <th>{t('spend.col.period')}</th>
                  <th className="num">{t('spend.col.declared')}</th>
                  <th>{t('spend.col.state')}</th>
                  <th className="num">{t('spend.col.approved')}</th>
                  <th>{t('spend.col.reason')}</th>
                </tr>
              </thead>
              <tbody>
                {declarations.value.items.map((row) => (
                  <tr key={row.id}>
                    <td>{row.deal_code}</td>
                    <td>
                      {formatDate(row.period_starts_at)} – {formatDate(row.period_ends_at)}
                    </td>
                    <td className="num money">{formatSpendAmount(row.declared_minor, row.currency)}</td>
                    <td>
                      {row.state === 'DECLARED' ? (
                        <Chip tone="provisional">{t('spend.state.declared')}</Chip>
                      ) : row.state === 'REJECTED' ? (
                        <Chip tone="overdue">{t('spend.state.rejected')}</Chip>
                      ) : (
                        <Chip tone="neutral">{t('spend.state.approved')}</Chip>
                      )}
                    </td>
                    <td className="num money">
                      {row.approved_minor !== null
                        ? formatSpendAmount(row.approved_minor, row.currency)
                        : '—'}
                    </td>
                    <td>{row.decision_reason ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        )}
      </Panel>
    </div>
  );
}
